/* =========================================================================
   The Moment Director — ONE arbiter for every interstitial (PLAN-gameplay-v2
   §3.0). Before this module each interstitial fired on its own rules and
   nothing stopped them stacking: a mini-game break, then a feast card, then a
   "Did You Know?", then the register nudge, all inside a minute of play.

   The Director is consulted at every question / round boundary and grants AT
   MOST ONE moment, subject to:
     • a 90s GLOBAL COOLDOWN between any two interstitials (measured from the
       start of the run, so the first minute and a half is pure play);
     • PRIORITY when several are due — prayer break → mini-game break →
       feast/DYK → GTKY → register nudge. Round mechanics (intro / results) are
       NOT moments and always run. Nothing is dropped, only deferred: the
       systems' own `>=` thresholds mean a declined moment simply asks again at
       the next boundary;
     • MINI-GAME BREAKS only when ≥ 10 min of active play has passed since the
       last break of any kind — the round's fixed `breakAt` becomes a break
       OPPORTUNITY the Director may decline;
     • PRAYER BREAKS only when ≥ 30 questions have been answered since the last
       one (a rolling LIFETIME counter, so it lands about once every three
       rounds), and never adjacent to the money wheel — reverence spacing defers
       it one slot.
     • GTKY ("a word with Theo") only when ≥ 50 questions have been answered
       since the last one — the SAME rolling-lifetime mechanism as the prayer
       counter, one tier quieter (PLAN-gameplay-v2 §6). WHO gets asked (the
       engaged-player gate), the one-per-day cap and the empty-pool silence all
       live in gtkyCadence.ts — the Director only owns the ~50 cadence, exactly
       as it owns the 30 for prayer.

   House pattern: the decision core is PURE over an injected clock/storage seam
   (mirrors dyk.ts / easyStart.ts / resumeGame.ts) and is heavily unit-tested;
   the live singleton at the bottom is the thin integration gameController uses.
   Eligibility for each system stays in that system's own module — the Director
   only owns TIMING.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';
import { currentIdentity } from './resumeGame';

/** Every interstitial the Director arbitrates, in no particular order. */
export type MomentKind = 'prayer' | 'minigame' | 'feastdyk' | 'gtky' | 'register';

/** Plan §3.0 priority, highest first. Round mechanics are not in this list —
    they are not moments and always run. */
export const MOMENT_PRIORITY: readonly MomentKind[] = ['prayer', 'minigame', 'feastdyk', 'gtky', 'register'];

/** ≥ 90s of play between ANY two interstitials. */
export const MOMENT_COOLDOWN_MS = 90_000;
/** ≥ ~10 min of active play since the last break of any kind. */
export const BREAK_INTERVAL_MS = 10 * 60_000;
/** ≥ 30 answered questions since the last prayer break (rolling lifetime). */
export const PRAYER_EVERY_ANSWERS = 30;
/** ≥ 50 answered questions since the last GTKY moment (rolling lifetime). */
export const GTKY_EVERY_ANSWERS = 50;

/* ----------------------------- pure decision core ----------------------------- */

/** The Director's working memory. Clocks are session-scoped (ms from the
    injected clock); the answered counter is lifetime and persisted. */
export interface DirectorMemory {
  /** Clock of the last granted moment of ANY kind (seeded to the run's start). */
  lastMomentAt: number;
  /** Clock of the last BREAK — mini-game or prayer (seeded to the run's start). */
  lastBreakAt: number;
  /** `answeredLifetime` as of the last prayer break (0 = never prayed yet). */
  prayerAnsweredAt: number;
  /** `answeredLifetime` as of the last GTKY moment (0 = never asked yet). */
  gtkyAnsweredAt: number;
}

/** One consultation. `candidates` is what the CALLING systems say is eligible
    right now by their own rules; the Director applies its own timing on top. */
export interface DirectorRequest {
  now: number;
  candidates: readonly MomentKind[];
  /** Rolling lifetime answered-question count (persisted per identity). */
  answeredLifetime: number;
  /** True when the moment in this slot is the money wheel — a prayer break is
      never served next to it and waits one slot. */
  wheelAdjacent?: boolean;
}

/** Has the player answered enough questions for another prayer break? PURE. */
export function prayerDue(answeredLifetime: number, prayerAnsweredAt: number): boolean {
  return answeredLifetime - prayerAnsweredAt >= PRAYER_EVERY_ANSWERS;
}

/** Has the player answered enough questions for another GTKY moment? PURE.
    NOTE: "asked" — an offered GTKY resets this whether the player answers,
    declines or taps "Maybe later", which is exactly the plan's respect rule:
    "Ask me later" → re-eligible after the NEXT 50, never sooner. */
export function gtkyDue(answeredLifetime: number, gtkyAnsweredAt: number): boolean {
  return answeredLifetime - gtkyAnsweredAt >= GTKY_EVERY_ANSWERS;
}

/** Has enough active play passed since the last break of any kind? PURE. */
export function breakDue(now: number, lastBreakAt: number): boolean {
  return now - lastBreakAt >= BREAK_INTERVAL_MS;
}

/** Is the global cooldown clear? PURE. */
export function cooldownClear(now: number, lastMomentAt: number): boolean {
  return now - lastMomentAt >= MOMENT_COOLDOWN_MS;
}

/**
 * The whole arbitration: at most one kind, or null when the slot stays pure
 * play. PURE — no clock, no storage, no DOM.
 */
export function decideMoment(req: DirectorRequest, mem: DirectorMemory): MomentKind | null {
  if (!cooldownClear(req.now, mem.lastMomentAt)) return null;
  const offered = new Set(req.candidates);
  for (const kind of MOMENT_PRIORITY) {
    if (!offered.has(kind)) continue;
    if (kind === 'prayer') {
      if (req.wheelAdjacent) continue; // reverence spacing — defer one slot
      if (!prayerDue(req.answeredLifetime, mem.prayerAnsweredAt)) continue;
    }
    if (kind === 'minigame' && !breakDue(req.now, mem.lastBreakAt)) continue;
    if (kind === 'gtky' && !gtkyDue(req.answeredLifetime, mem.gtkyAnsweredAt)) continue;
    return kind;
  }
  return null;
}

/**
 * The memory AFTER granting `kind` — a fresh object (never mutates). Skipping a
 * granted mini-game break still lands here: the pause counted, so the break
 * clock resets either way. PURE.
 */
export function applyGrant(kind: MomentKind, req: DirectorRequest, mem: DirectorMemory): DirectorMemory {
  const next: DirectorMemory = { ...mem, lastMomentAt: req.now };
  if (kind === 'prayer' || kind === 'minigame') next.lastBreakAt = req.now;
  if (kind === 'prayer') next.prayerAnsweredAt = req.answeredLifetime;
  if (kind === 'gtky') next.gtkyAnsweredAt = req.answeredLifetime;
  return next;
}

/* ------------------------------- storage seam ------------------------------- */

/** What survives between runs: the rolling counters (never the session clocks). */
export interface DirectorCounters {
  answered: number;
  prayerAnsweredAt: number;
  /** Missing on counter files written before the GTKY cadence (v1.1.0) — those
      read back as 0, i.e. a long-standing player is due at the next boundary.
      The gtkyCadence day-cap keeps that from being a burst. */
  gtkyAnsweredAt: number;
}

export const MOMENTS_KEY_PREFIX = 'catholics.moments:';

/** Per-identity key (userId when authed, `guest:<guestId>` otherwise) so a
    sign-in never inherits a stranger's pacing. PURE. */
export function momentsKeyFor(identity: string): string {
  return MOMENTS_KEY_PREFIX + identity;
}

/** Defensive read — anything off-shape restarts the counters at zero. PURE. */
export function parseCounters(raw: string | null | undefined): DirectorCounters {
  const zero: DirectorCounters = { answered: 0, prayerAnsweredAt: 0, gtkyAnsweredAt: 0 };
  if (!raw) return zero;
  try {
    const p = JSON.parse(raw) as unknown;
    if (!p || typeof p !== 'object') return zero;
    const r = p as Record<string, unknown>;
    const answered = typeof r.answered === 'number' && Number.isFinite(r.answered) ? Math.max(0, Math.floor(r.answered)) : 0;
    const nonNeg = (v: unknown): number =>
      typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
    // A counter file that claims more prayers/asks than answers is corrupt — clamp.
    return {
      answered,
      prayerAnsweredAt: Math.min(nonNeg(r.prayerAnsweredAt), answered),
      gtkyAnsweredAt: Math.min(nonNeg(r.gtkyAnsweredAt), answered),
    };
  } catch {
    return zero;
  }
}

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/* --------------------------------- the live seam --------------------------------- */

export interface DirectorDeps {
  /** Monotonic-ish ms clock (default Date.now — active play is wall clock here). */
  now?: () => number;
  store?: KVStore | null;
  /** Identity for the persisted counters (default: the live player identity). */
  identity?: () => string;
}

export interface Director {
  /** Start (or restart) a run: session clocks reset, counters reload. */
  beginSession(): void;
  /** One answered question — the prayer-break cadence signal. */
  noteAnswered(): void;
  /** Consult at a boundary. Returns the granted kind (already committed to
      memory) or null when this slot stays pure play. */
  consult(candidates: readonly MomentKind[], opts?: { wheelAdjacent?: boolean }): MomentKind | null;
  /** Commit a moment the CALLER decided to run outside arbitration (today: the
      one-time Theo introduction, which rides the first break of a first game).
      Same bookkeeping as a granted moment, so the clocks stay honest. */
  grant(kind: MomentKind): void;
  /** The rolling lifetime answered count (tests + telemetry). */
  answeredLifetime(): number;
  /** Read-only view of the working memory (tests). */
  memory(): DirectorMemory;
}

/** Build a Director over injected seams. The exported singleton below is the
    one the game uses; tests build their own with a fake clock/store. */
export function createMomentDirector(deps: DirectorDeps = {}): Director {
  const now = deps.now ?? (() => Date.now());
  const identity = deps.identity ?? currentIdentity;
  const store = deps.store === undefined ? deviceStore() : deps.store;

  let counters: DirectorCounters = { answered: 0, prayerAnsweredAt: 0, gtkyAnsweredAt: 0 };
  let mem: DirectorMemory = { lastMomentAt: 0, lastBreakAt: 0, prayerAnsweredAt: 0, gtkyAnsweredAt: 0 };
  let key = '';
  let loaded = false;

  function load(): void {
    key = momentsKeyFor(identity());
    let raw: string | null = null;
    try {
      raw = store ? store.getItem(key) : null;
    } catch {
      raw = null; // private mode — pacing simply restarts
    }
    counters = parseCounters(raw);
    mem.prayerAnsweredAt = counters.prayerAnsweredAt;
    mem.gtkyAnsweredAt = counters.gtkyAnsweredAt;
    loaded = true;
  }

  function save(): void {
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(counters));
    } catch {
      /* private mode / eviction — the counters just don't persist */
    }
  }

  function ensure(): void {
    if (!loaded) load();
  }

  /** Mirror the rolling cadence marks a granted moment moved into the persisted
      counters (prayer's 30 and GTKY's 50 both survive the session). */
  function persistCadence(kind: MomentKind): void {
    if (kind !== 'prayer' && kind !== 'gtky') return;
    counters.prayerAnsweredAt = mem.prayerAnsweredAt;
    counters.gtkyAnsweredAt = mem.gtkyAnsweredAt;
    save();
  }

  return {
    beginSession(): void {
      load(); // identity may have changed since the last run (sign-in / sign-out)
      const t = now();
      // Seeding BOTH clocks to the run's start buys the player 90s of unbroken
      // play before any interstitial, and 10 min before the first mini-game break.
      mem = {
        lastMomentAt: t,
        lastBreakAt: t,
        prayerAnsweredAt: counters.prayerAnsweredAt,
        gtkyAnsweredAt: counters.gtkyAnsweredAt,
      };
    },
    noteAnswered(): void {
      ensure();
      counters.answered++;
      save();
    },
    consult(candidates, opts): MomentKind | null {
      ensure();
      const req: DirectorRequest = {
        now: now(),
        candidates,
        answeredLifetime: counters.answered,
        wheelAdjacent: opts?.wheelAdjacent,
      };
      const grant = decideMoment(req, mem);
      if (!grant) return null;
      mem = applyGrant(grant, req, mem);
      persistCadence(grant);
      return grant;
    },
    grant(kind): void {
      ensure();
      const req: DirectorRequest = { now: now(), candidates: [kind], answeredLifetime: counters.answered };
      mem = applyGrant(kind, req, mem);
      persistCadence(kind);
    },
    answeredLifetime(): number {
      ensure();
      return counters.answered;
    },
    memory(): DirectorMemory {
      ensure();
      return { ...mem };
    },
  };
}

/** The game's Director. gameController drives it; nothing else should. */
export const momentDirector: Director = createMomentDirector();
